import { EMPTY_TILE, TILE_FLIP_X, TILE_FLIP_Y, unpackTile } from "../document/packed-tile.ts";
import { frameId, type AssetManifest, type FrameRect } from "./manifest.ts";

/**
 * Iterates the frames a block of packed cells draws, in row-major order, with no allocation per cell: the
 * pure core of chunk painting, shared by the Phaser renderer and the panels' 2D canvas samples.
 */

/**
 * A block of packed cells and what they are packed against. `missing`, when given, is told the index of each
 * cell whose frame the manifest lacks, and that cell is passed over; without it such a cell is an error. An
 * editor whose atlas is older than its document gives it, and draws those cells as boxes.
 */
export interface StampCells {
  readonly tiles: Uint32Array;
  readonly chunkSize: number;
  readonly tileSize: number;
  readonly tilesets: readonly string[];
  readonly variant: string | null;
  readonly manifest: AssetManifest;
  readonly missing?: ((cell: number) => void) | undefined;
}

/**
 * Calls `emit` for every non-empty cell with its page, frame id, rect, pixel origin inside the block and
 * mirroring (ADR-089): a mirrored cell draws its frame mirrored over the same rectangle.
 *
 * @param cells The block.
 * @param emit Receives page index, frame id, frame rect, the cell's pixel origin and its horizontal and vertical
 *   mirroring.
 * @throws Error when the cell count is not `chunkSize * chunkSize`, a cell names a tileset index outside
 *   the table, or the manifest lacks the frame and the block has no `missing`.
 */
export function chunkStampsOf(cells: StampCells, emit: (page: number, frame: string, rect: FrameRect, x: number, y: number, flipX: boolean, flipY: boolean) => void): void {
  const { tiles, chunkSize, tileSize, tilesets, variant, manifest, missing } = cells;
  if (tiles.length !== chunkSize * chunkSize) {
    throw new Error(`chunk has ${tiles.length} cells, expected ${chunkSize * chunkSize}`);
  }
  for (let i = 0; i < tiles.length; i += 1) {
    const packed = tiles[i] as number;
    if (packed === EMPTY_TILE) {
      continue;
    }
    const { tilesetIndex, tile } = unpackTile(packed);
    const tileset = tilesets[tilesetIndex];
    if (tileset === undefined) {
      throw new Error(`cell ${i} names tileset index ${tilesetIndex}, but the chunk lists ${tilesets.length} tilesets`);
    }
    const id = frameId(tileset, variant, tile);
    const rect = manifest.frames[id];
    if (rect === undefined) {
      if (missing === undefined) {
        throw new Error(`asset manifest has no frame ${id}`);
      }
      missing(i);
      continue;
    }
    emit(rect.page, id, rect, (i % chunkSize) * tileSize, Math.floor(i / chunkSize) * tileSize, (packed & TILE_FLIP_X) !== 0, (packed & TILE_FLIP_Y) !== 0);
  }
}
